import React, { useState, useEffect } from 'react';
import { Search, Globe, ExternalLink, BarChart3, Zap } from 'lucide-react';
import { DOMAINS } from '../config/domains';

interface DomainStats {
  domain: string;
  authority: number;
  traffic: string;
  status: 'active' | 'new';
  lastPost: string;
}

const NetworkShowcase: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('authority');
  const [networkStats, setNetworkStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Generate mock data for domains with realistic stats
  const generateDomainStats = (): DomainStats[] => {
    return DOMAINS.map((domain, index) => ({
      domain,
      authority: Math.floor(Math.random() * 30) + 50, // 50-80 DR score
      traffic: `${Math.floor(Math.random() * 80) + 20}K`, // 20K-100K traffic
      status: index < 8 ? 'new' : 'active', // First 8 are new, rest are active
      lastPost: `${Math.floor(Math.random() * 3) + 1} days ago`, // 1-3 days ago
    }));
  };

  const [domainStats] = useState<DomainStats[]>(generateDomainStats());

  useEffect(() => {
    fetchNetworkStats();
  }, []);

  const fetchNetworkStats = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/network/stats`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setNetworkStats(result.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch network stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDomains = domainStats.filter(domain => {
    const matchesSearch = domain.domain.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const sortedDomains = [...filteredDomains].sort((a, b) => {
    switch (sortBy) {
      case 'authority':
        return b.authority - a.authority;
      case 'traffic':
        return parseInt(b.traffic) - parseInt(a.traffic);
      case 'recent':
        return parseInt(a.lastPost) - parseInt(b.lastPost);
      case 'alphabetical':
        return a.domain.localeCompare(b.domain);
      default:
        return 0;
    }
  });

  const totalAuthority = domainStats.reduce((sum, d) => sum + d.authority, 0);
  const averageAuthority = Math.round(totalAuthority / domainStats.length);
  const activeDomains = domainStats.filter(d => d.status === 'active').length;
  const newDomains = domainStats.filter(d => d.status === 'new').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Our Citation Network
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              301 High-Authority Citation Domains Ready to Boost Your Rankings
            </p>
            
            {/* Network Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {loading ? '...' : networkStats?.network_overview?.total_domains || DOMAINS.length}
                </div>
                <div className="text-blue-200">Total Domains</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {loading ? '...' : networkStats?.network_overview?.average_authority || '60'}
                </div>
                <div className="text-blue-200">Avg DR Score</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {loading ? '...' : networkStats?.network_overview?.total_monthly_traffic || '2.5M+'}
                </div>
                <div className="text-blue-200">Monthly Visitors</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {loading ? '...' : networkStats?.network_overview?.uptime_percentage || '99.9'}%
                </div>
                <div className="text-blue-200">Uptime</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search citation domains..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-4 items-center">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="authority">Sort by DR Score</option>
                <option value="traffic">Sort by Traffic</option>
                <option value="recent">Sort by Recent Activity</option>
                <option value="alphabetical">Sort A-Z</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            Showing {sortedDomains.length} of {DOMAINS.length} citation domains
          </div>
        </div>

        {/* Network Performance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Network Health</h3>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Active Domains</span>
                <span className="font-semibold text-green-600">{activeDomains}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">New Domains</span>
                <span className="font-semibold text-blue-600">{newDomains}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">All Live</span>
                <span className="font-semibold text-green-600">100%</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Authority Distribution</h3>
              <BarChart3 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">High DR (70+)</span>
                <span className="font-semibold text-green-600">
                  {domainStats.filter(d => d.authority >= 70).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Medium DR (60-69)</span>
                <span className="font-semibold text-yellow-600">
                  {domainStats.filter(d => d.authority >= 60 && d.authority < 70).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Good DR (50-59)</span>
                <span className="font-semibold text-blue-600">
                  {domainStats.filter(d => d.authority >= 50 && d.authority < 60).length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Content Performance</h3>
              <Zap className="w-5 h-5 text-purple-500" />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Recent Activity</span>
                <span className="font-semibold text-purple-600">
                  {domainStats.filter(d => parseInt(d.lastPost) <= 3).length} domains
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Average DR</span>
                <span className="font-semibold text-blue-600">
                  {Math.round(domainStats.reduce((sum, d) => sum + d.authority, 0) / domainStats.length)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fresh Content</span>
                <span className="font-semibold text-green-600">
                  Daily Updates
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Citation Domains List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-6 font-semibold text-gray-900">Domain</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-900">DR Score</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-900">Traffic</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-900">Last Updated</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-900">Status</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-900">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDomains.map((domain, index) => (
                    <tr key={domain.domain} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-900">{domain.domain}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <div className="w-12 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ width: `${(domain.authority / 80) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold">{domain.authority}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-green-600 font-semibold">{domain.traffic}</td>
                      <td className="py-4 px-6 text-gray-600">{domain.lastPost}</td>
                      <td className="py-4 px-6">
                        <div className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${
                          domain.status === 'active' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {domain.status}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <a
                          href={`https://${domain.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Leverage Our Network?</h2>
          <p className="text-xl text-blue-100 mb-6">
            Start your SEO campaign today and tap into the power of 301 high-authority domains
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors">
              Schedule Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkShowcase;
